/**
 * AutocompleteSearch component provides an autocomplete search functionality using Material-UI's Autocomplete and TextField components.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.optionsLabel - The label key for the options.
 * @param {string} props.optionsName - The name key for the options.
 * @param {string} props.keyLabel - The key used for the option's unique identifier.
 * @param {Function} props.searchStore - The function to perform the search.
 * @param {Array} props.responseStore - The array of search results.
 * @param {Function} props.addResultFunction - The function to handle the selected result.
 * @param {string} props.displayLabel - The label to display on the TextField.
 * @param {Function} [props.customSearchItems] - Optional custom search function.
 * @param {boolean} props.multipleResponse - Flag to allow multiple selections.
 *
 * @returns {JSX.Element} The rendered AutocompleteSearch component.
 */

import { Autocomplete, TextField } from "@mui/material";
import * as React from 'react';

export default function AutocompleteSearch({
    optionsLabel,
    optionsName,
    keyLabel,
    searchStore,
    responseStore,
    addResultFunction,
    displayLabel,
    customSearchItems,
    multipleResponse 
  }: any) {

  const [results, setResult] = React.useState([]);
  const [searchInput, setSearchInput] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const loading = open && searchInput.length > 2 && results != ([]);

  React.useEffect(() => {
    let active = true;
    if (!loading) {
      return undefined;
    }
    (async () => {
      if (active) {
        setResult([]);
      }
    })();
    return () => {
      active = false;
    };
  }, [loading]);

  function searchItems(_event: React.SyntheticEvent, value: string) {
    setSearchInput(value)
    if (customSearchItems){
      customSearchItems(_event, value);
    } else {
      if (value !== '' && value.length > 2) {
        searchStore(value).then(() => {
          if (responseStore) {
            setResult(responseStore);
          }
        });
      }
    }

  }

  return (
    <Autocomplete
      multiple={multipleResponse}
      fullWidth={true}
      id="autocomplete-search"
      options={responseStore ? responseStore : []}
      getOptionLabel={(option: any) => option[optionsLabel] || ""}
      isOptionEqualToValue={(option, name) => option[optionsLabel] === name[optionsLabel]}
      onInputChange={searchItems}
      onChange={(_event, value) => {
        addResultFunction(value);
      }}
      filterOptions={(options) => options}// no filtering option to fix GED-46
      loading={loading}
      open={open}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
      }}
      renderOption={(props, option) => {
        return (
          optionsName === "" ? (
            <li {...props} key={option[keyLabel]}>
              {option[optionsLabel]}
            </li> ) : (
            <li {...props} key={option[keyLabel]}>
              {option[optionsName]+ " - " +option[optionsLabel]}
            </li>
          )
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          label={displayLabel}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />)
}